import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { AlertColor, Button, Snackbar, TextField } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { NestedValue, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { GENERATE_RESET_PASSCODE } from "../../graphql/mutations/auth/auth.mutations";
import Loader from "../loader/loader.component";
import { useDispatch } from "react-redux";
import { isHomeHeader } from "../../redux/actions";
import Alert from "../alert/alert.component";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>("");
  const [severity, setSeverity] = useState<AlertColor>("error");

  const navigate = useNavigate();

  const openAlert = (error: any) => {
    setError(error);
    setAlertOpen(true);
  };

  const closeAlert = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setAlertOpen(false);
  };

  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(isHomeHeader(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<{
    email: NestedValue<any>;
  }>({
    defaultValues: {
      email: "",
    },
  });

  const [generateResetPasscode, { loading, error: generateCodeError }] =
    useMutation(GENERATE_RESET_PASSCODE, {
      onCompleted: () => {
        setSeverity("success");
        openAlert("Successfully Generated");
        setTimeout(() => {
          navigate("/reset-password");
        }, 2000);
      },
    });

  React.useEffect(() => {
    if (generateCodeError?.message) {
      setSeverity("error");
      openAlert(generateCodeError?.message);
    }
  }, [generateCodeError]);

  React.useEffect(() => {
    register("email", {
      validate: (value) => value.length || "Email is required.",
    });
  }, [register]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await generateResetPasscode({ variables: { email: data.email } });
    } catch (err) {}
  });

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          background: "var(--color-neutral-100)",
          padding: "100px 20px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Card
          variant="outlined"
          sx={{
            maxWidth: 400,
            width: "100%",
            padding: 4,
            bgcolor: "var(--color-neutral-50)",
            border: "1px solid var(--color-rule)",
            borderRadius: "10px",
            boxShadow: "0 1px 0 rgba(28,25,23,0.02)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <form
            onSubmit={onSubmit}
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <LockIcon sx={{ color: "var(--color-neutral-700)" }} />
              <label
                style={{
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize: 22,
                  color: "var(--color-neutral-900)",
                  letterSpacing: "-0.01em",
                }}
              >
                Reset your password
              </label>
            </Box>
            <TextField
              {...register("email")}
              id="outlined-basic"
              label="Email"
              variant="outlined"
              type={"email"}
              error={!!errors?.email}
              helperText={!!errors?.email ? errors?.email?.message : ""}
            />
            {loading ? (
              <Loader />
            ) : (
              <Button
                variant="contained"
                sx={{
                  height: 44,
                  bgcolor: "var(--color-primary-400)",
                  color: "var(--color-green-ink, #0B2B17)",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: 14,
                  borderRadius: "8px",
                  boxShadow: "0 1px 0 rgba(15,45,22,0.08), 0 1px 2px rgba(15,45,22,0.06)",
                  "&:hover": {
                    bgcolor: "var(--color-primary-500)",
                  },
                }}
                type={"submit"}
              >
                Send
              </Button>
            )}
          </form>
        </Card>
      </Box>
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={closeAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={closeAlert} severity={severity} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ForgotPassword;
